import React, { useState, useEffect } from 'react';
import './ProductFilters.css';
import { productGroupsAPI } from '../../utils/api';

const ProductFilters = ({ filters, onFilterChange, onClearAll }) => {
  const [availableFilters, setAvailableFilters] = useState({
    vendors: [],
    departments: [],
    sizes: [],
    priceRange: { min: 0, max: 1000 }
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAvailableFilters();
  }, []);

  const loadAvailableFilters = async () => {
    try {
      const response = await productGroupsAPI.getAvailableFilters();
      const data = response.data?.data || {};
      setAvailableFilters({
        vendors: data.vendors || [],
        departments: data.departments || [],
        sizes: data.sizes || [],
        priceRange: data.priceRange || { min: 0, max: 1000 }
      });
    } catch (error) {
      console.error('Error loading filters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVendorToggle = (vendor) => {
    const currentVendors = filters.vendor ? filters.vendor.split(',') : [];
    const newVendors = currentVendors.includes(vendor)
      ? currentVendors.filter(v => v !== vendor)
      : [...currentVendors, vendor];
    
    onFilterChange('vendor', newVendors.length > 0 ? newVendors.join(',') : null);
  };

  const handleDepartmentChange = (department) => {
    onFilterChange('department', department === filters.department ? null : department);
  };

  const handlePriceChange = (type, value) => {
    onFilterChange(type, value || null);
  };

  const handleInStockToggle = () => {
    onFilterChange('inStock', filters.inStock ? null : '1');
  };

  const handleSortChange = (sort) => {
    onFilterChange('sort', sort);
  };

  if (isLoading) {
    return <div className="filters-loading">Loading filters...</div>;
  }

  const selectedVendors = filters.vendor ? filters.vendor.split(',') : [];

  return (
    <div className="product-filters">
      <div className="filters-header">
        <h3>Filters</h3>
        {(filters.vendor || filters.department || filters.priceMin || filters.priceMax || filters.inStock) && (
          <button className="clear-all-btn" onClick={onClearAll}>
            Clear all
          </button>
        )}
      </div>

      {/* Sort */}
      <div className="filter-section">
        <h4>Sort By</h4>
        <select 
          className="sort-select"
          value={filters.sort || 'featured'}
          onChange={(e) => handleSortChange(e.target.value)}
        >
          <option value="featured">Featured</option>
          <option value="priceAsc">Price: Low to High</option>
          <option value="priceDesc">Price: High to Low</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      {/* In Stock */}
      <div className="filter-section">
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={filters.inStock === '1'}
            onChange={handleInStockToggle}
          />
          <span>In Stock Only</span>
        </label>
      </div>

      {/* Vendors (Brands) */}
      <div className="filter-section">
        <h4>Brand</h4>
        <div className="filter-options">
          {availableFilters.vendors.slice(0, 10).map((vendor) => {
            const name = vendor.vendor_name || vendor;
            const slug = vendor.vendor_slug || vendor;
            return (
              <label key={slug} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedVendors.includes(slug)}
                  onChange={() => handleVendorToggle(slug)}
                />
                <span>{name}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Departments (Categories) */}
      <div className="filter-section">
        <h4>Category</h4>
        <div className="filter-options scrollable">
          {availableFilters.departments.slice(0, 15).map((department) => (
            <label key={department} className="filter-radio">
              <input
                type="radio"
                name="department"
                checked={filters.department === department}
                onChange={() => handleDepartmentChange(department)}
              />
              <span>{department}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div className="filter-section">
        <h4>Price Range</h4>
        <div className="price-inputs">
          <input
            type="number"
            placeholder="Min"
            value={filters.priceMin || ''}
            onChange={(e) => handlePriceChange('priceMin', e.target.value)}
            min="0"
          />
          <span>to</span>
          <input
            type="number"
            placeholder="Max"
            value={filters.priceMax || ''}
            onChange={(e) => handlePriceChange('priceMax', e.target.value)}
            min="0"
          />
        </div>
        <div className="price-range-hint">
          ${availableFilters.priceRange.min} - ${availableFilters.priceRange.max}
        </div>
      </div>
    </div>
  );
};

export default ProductFilters;
